import { Text } from '@customerio/pluma-components/react';
import { isTwoValueFilterOperator, isUnaryFilterOperator } from '../../attribute-filter-state';
import { isOneOfOperator } from '../../operators';
import {
  isAbsoluteTimestampOperator,
  isRelativeTimestampOperator,
  isTimestampInequalityOperator,
} from '../../relative-timestamp';
import { OneOfValueSelect } from './one-of-value-select';
import { AbsoluteTimestampValue, RelativeTimestampValue, TimestampRangeValue } from './timestamp-editors';
import { ValueCombobox } from './value-combobox';
import type { ConditionControlSize, OperatorValuePatch, ValueOption } from './types';

export type ValueEditorProps = {
  field: string;
  operator: string;
  value: string | null;
  value2: string | null;
  valueOptions: ValueOption[];
  isLoadingValueOptions?: boolean;
  disabled?: boolean;
  valueError?: string;
  /** What the value field says while empty; "Value" by default. */
  valuePlaceholder?: string;
  onChange: (patch: OperatorValuePatch) => void;
  onValueBlur: () => void;
  size?: ConditionControlSize;
};

/**
 * The value half of a condition row — whichever editor the operator needs.
 *
 * A unary operator (`exists`) renders nothing: it takes no value, and the one
 * it may carry off the wire is never shown.
 */
export function ValueEditor({
  field,
  operator,
  value,
  value2,
  valueOptions,
  isLoadingValueOptions,
  disabled,
  valueError,
  valuePlaceholder,
  onChange,
  onValueBlur,
  size = 'sm',
}: ValueEditorProps) {
  if (isUnaryFilterOperator(operator)) {
    return null;
  }

  if (operator === 'timestamp_bw') {
    return <TimestampRangeValue disabled={disabled} value={value} value2={value2} onChange={onChange} size={size} />;
  }

  if (isRelativeTimestampOperator(operator)) {
    return (
      <RelativeTimestampValue
        disabled={disabled}
        field={field}
        operator={operator}
        value={value}
        onChange={onChange}
        size={size}
      />
    );
  }

  if (isAbsoluteTimestampOperator(operator)) {
    return <AbsoluteTimestampValue disabled={disabled} value={value} onChange={onChange} size={size} />;
  }

  // A timestamp inequality with no variant resolved yet renders only its
  // variant select, which the operator row owns.
  if (isTimestampInequalityOperator(operator)) {
    return null;
  }

  if (isOneOfOperator(operator)) {
    return (
      <OneOfValueSelect
        disabled={disabled}
        value={value}
        valueOptions={valueOptions}
        isLoadingValueOptions={isLoadingValueOptions}
        error={valueError}
        onChange={onChange}
        onTouched={onValueBlur}
        size={size}
      />
    );
  }

  if (isTwoValueFilterOperator(operator)) {
    return (
      <>
        <ValueCombobox
          ariaLabel="Value"
          testId="dryad-filter-value-input"
          placeholder={valuePlaceholder}
          disabled={disabled}
          inputValue={value}
          isLoadingValueOptions={isLoadingValueOptions}
          valueOptions={valueOptions}
          error={valueError}
          onValueChange={(nextValue) => onChange({ value: nextValue })}
          onBlur={onValueBlur}
          size={size}
        />
        <Text as="span" color="subtle">
          and
        </Text>
        <ValueCombobox
          ariaLabel="Second value"
          disabled={disabled}
          inputValue={value2}
          isLoadingValueOptions={isLoadingValueOptions}
          valueOptions={valueOptions}
          onValueChange={(nextValue) => onChange({ value2: nextValue })}
          size={size}
        />
      </>
    );
  }

  return (
    <ValueCombobox
      ariaLabel="Value"
      testId="dryad-filter-value-input"
      placeholder={valuePlaceholder}
      disabled={disabled}
      inputValue={value}
      isLoadingValueOptions={isLoadingValueOptions}
      valueOptions={valueOptions}
      error={valueError}
      onValueChange={(nextValue) => onChange({ value: nextValue })}
      onBlur={onValueBlur}
      size={size}
    />
  );
}
